import React, { useEffect, useState } from "react"
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useFormik } from "lib/formik"
import { ROUTES } from "constants/routes"
import { useOnboarding } from "context/OnboardingContext"
import { clearOnboardingIfPageReloaded } from "helpers/onboardingSession"
import {
  onboardingPost,
  hasOnboardingRegistration,
  saveOnboardingRegistration,
  getOnboardingApiMessage,
  getOnboardingTenantId,
} from "helpers/onboarding_api_helper"
import {
  buildTenantFormSchema,
  mapTenantApiErrorToField,
} from "utils/tenantValidation"
import { EMAIL_TOTAL_MAX_LENGTH } from "utils/emailValidation"
import OnboardingLayout from "./OnboardingLayout"

const RegisterTenant = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    formDraft,
    setFormDraft,
    registeredSnapshot,
    setRegisteredSnapshot,
    resetFlow,
  } = useOnboarding()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    document.title = `${t("Register")} | Wulooj`
    if (clearOnboardingIfPageReloaded()) {
      resetFlow()
    } else if (location.state?.startFresh) {
      resetFlow()
    }
  }, [])

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: formDraft,
    validationSchema: buildTenantFormSchema(t, {
      includeStatus: false,
      includeOrderNumbering: false,
    }),
    onSubmit: async (values, helpers) => {
      setSubmitting(true)
      setError("")
      setFormDraft(values)

      const applyRegistrationError = response => {
        const { field, message } = mapTenantApiErrorToField(response, t)
        const translatedMessage = t(message, { defaultValue: message })

        if (field) {
          helpers.setFieldError(field, translatedMessage)
          helpers.setFieldTouched(field, true, false)
        }

        setError(
          translatedMessage ||
            getOnboardingApiMessage(response) ||
            t("Registration failed. Please try again.")
        )
      }

      const existingTenantId = getOnboardingTenantId()
      const unchangedSinceRegister =
        hasOnboardingRegistration() &&
        registeredSnapshot &&
        JSON.stringify(values) === JSON.stringify(registeredSnapshot)

      if (unchangedSinceRegister) {
        navigate(ROUTES.REGISTER_PLANS)
        setSubmitting(false)
        return
      }

      try {
        const res = await onboardingPost("onboarding/register", {
          ...values,
          existingTenantId: existingTenantId || undefined,
        })
        if (!res?.status) {
          applyRegistrationError(res)
          return
        }

        const data = res.data || {}
        saveOnboardingRegistration({
          onboardingToken: data.onboardingToken,
          tenant: data.tenant,
        })
        setRegisteredSnapshot(values)

        if (!hasOnboardingRegistration()) {
          applyRegistrationError(res)
          return
        }

        navigate(ROUTES.REGISTER_PLANS)
      } catch (err) {
        const apiBody = err?.response?.data || err
        applyRegistrationError(apiBody)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleFormSubmit = e => {
    e.preventDefault()
    formik.handleSubmit(e)
  }

  const handleContinueClick = async () => {
    setError("")
    const errors = await formik.validateForm()
    const errorKeys = Object.keys(errors)
    if (errorKeys.length) {
      await formik.setTouched(
        errorKeys.reduce((acc, key) => {
          acc[key] = true
          return acc
        }, { ...formik.touched }),
        false
      )
      const firstError = errors[errorKeys[0]]
      setError(
        typeof firstError === "string"
          ? firstError
          : t("Please check the form and try again.")
      )
      return
    }
    formik.handleSubmit()
  }

  return (
    <OnboardingLayout>
      <div className="onboarding-card onboarding-card--scrollable">
        <div className="onboarding-steps">
          <span className="step-pill active">{t("1. Business details")}</span>
          <span className="step-pill">{t("2. Choose plan")}</span>
          <span className="step-pill">{t("3. Payment")}</span>
        </div>

        <h4 className="mb-1">{t("Create your tenant account")}</h4>
        <p className="text-muted mb-4">
          {t("Tell us about your business to get started.")}
        </p>

        <Form
          className="onboarding-register-form"
          onSubmit={handleFormSubmit}
          noValidate
        >
          <div className="onboarding-card-scroll-area">
            {error && <div className="alert alert-danger">{error}</div>}

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Tenant Name")} *</Label>
                  <Input
                    name="name"
                    placeholder={t("Enter tenant name")}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.name && !!formik.errors.name}
                  />
                  <FormFeedback>{formik.errors.name}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Domain")} *</Label>
                  <Input
                    name="domain"
                    placeholder={t("e.g. acme.halfmillion.app")}
                    value={formik.values.domain}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.domain && !!formik.errors.domain}
                  />
                  <FormFeedback>{formik.errors.domain}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Slug")} *</Label>
                  <Input
                    name="slug"
                    placeholder={t("e.g. acme-store")}
                    value={formik.values.slug}
                    onChange={e => {
                      formik.setFieldValue(
                        "slug",
                        e.target.value.toLowerCase()
                      )
                    }}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.slug && !!formik.errors.slug}
                  />
                  <FormFeedback>{formik.errors.slug}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Contact Email")} *</Label>
                  <Input
                    name="contactEmail"
                    type="email"
                    maxLength={EMAIL_TOTAL_MAX_LENGTH}
                    placeholder={t("admin@tenant.com")}
                    value={formik.values.contactEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.contactEmail &&
                      !!formik.errors.contactEmail
                    }
                  />
                  <FormFeedback>{formik.errors.contactEmail}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{t("Tenant Admin Email")} *</Label>
                  <Input
                    name="adminEmail"
                    type="email"
                    maxLength={EMAIL_TOTAL_MAX_LENGTH}
                    placeholder={t("tenant-admin@company.com")}
                    value={formik.values.adminEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.adminEmail && !!formik.errors.adminEmail
                    }
                  />
                  <FormFeedback>{formik.errors.adminEmail}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>
          </div>

          <div className="d-flex gap-2 onboarding-card-footer">
            <Button
              color="primary"
              type="button"
              disabled={submitting}
              onClick={handleContinueClick}
            >
              {submitting ? <Spinner size="sm" /> : t("Continue to plans")}
            </Button>
            <Button
              color="secondary"
              outline
              type="button"
              onClick={() => navigate(ROUTES.LANDING)}
            >
              {t("Cancel")}
            </Button>
          </div>
        </Form>
      </div>
    </OnboardingLayout>
  )
}

export default RegisterTenant
